import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gameClockHHMM, msUntilTick } from '@jetopolis/sim';
import { useWorld } from '../app/stores/world';

/**
 * Единая шапка мира (05 §5): дата, игровое время, обратный отсчёт до тика,
 * онлайн и состояние связи.
 *
 * Обратный отсчёт тикает локально раз в секунду от абсолютного nextTickAt,
 * присланного сервером: спрашивать сервер каждую секунду незачем, а расхождение
 * выправляет ближайший пуш.
 */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

function formatLeft(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

export function TopBar() {
  const { t } = useTranslation();
  const now = useNow();
  const { world, online, nextTickAt, connection } = useWorld();

  if (!world) return null;

  const tickStart = nextTickAt === null ? now : nextTickAt - world.tickMinutes * 60_000;
  const clock = nextTickAt === null ? '23:59' : gameClockHHMM(tickStart, world.tickMinutes, now);
  const left = nextTickAt === null ? 0 : msUntilTick(nextTickAt, now);
  const progress = nextTickAt === null ? 1 : 1 - Math.min(1, left / (world.tickMinutes * 60_000));

  const dot =
    connection === 'online' ? 'bg-green' : connection === 'connecting' ? 'bg-amber' : 'bg-red';

  return (
    <header className="relative z-20 flex h-[calc(var(--spacing-topbar)+env(safe-area-inset-top))] items-end border-b border-line bg-ink/95 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="flex h-[var(--spacing-topbar)] w-full items-center gap-3 px-3">
        <span className="font-[family-name:var(--font-display)] text-[13px] font-bold tracking-[0.08em] text-amber">
          JETOPOLIS
        </span>

        <span className="hidden text-[11px] text-mut sm:inline">{world.name}</span>

        <div className="ms-auto flex items-center gap-3">
          <div className="flex flex-col items-end leading-tight">
            <span className="tabular text-[13px] text-txt">{world.gameDate}</span>
            <span className="tabular text-[10px] text-dim">{clock}</span>
          </div>

          <span className="tabular hidden text-[11px] text-mut sm:inline">
            {nextTickAt === null
              ? t('hq.status.FINISHED')
              : t('common.time.untilTick', { time: formatLeft(left) })}
          </span>

          <span className="tabular text-[11px] text-cyan">
            {t('common.label.online', { count: online })}
          </span>

          <span
            className={`h-2 w-2 rounded-full ${dot}`}
            role="status"
            aria-label={t(`common.state.${connection}`)}
          />
        </div>
      </div>

      {/* Тонкая полоса прогресса дня под шапкой. */}
      <div
        className="absolute inset-x-0 bottom-0 h-px bg-amber/70 transition-[width] duration-1000 ease-linear"
        style={{ width: `${progress * 100}%` }}
      />
    </header>
  );
}
